import { endOfDay, format, startOfDay } from 'date-fns';
import type { TabularCell } from '@/lib/export/tabularExport';
import type { ColumnConfig, ColumnSortState } from './types';

/** Inclusive From/To range of a `filterVariant: 'dateRange'` column filter. */
export interface ColumnDateRange {
  from?: Date;
  to?: Date;
}

const CALENDAR_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Effective filter UI for a `filterable` column. `type: 'date'` columns
 * default to the date-range popover (a unique-value checklist would list
 * every distinct day); everything else defaults to the value checklist.
 * An explicit `filterVariant` always wins — e.g. `'dateRange'` on a
 * text-typed timestamp column, or `'values'` to force the checklist on a
 * date column.
 */
export function resolveFilterVariant(
  col: Pick<ColumnConfig, 'type' | 'filterVariant'>,
): 'values' | 'dateRange' {
  if (col.filterVariant) return col.filterVariant;
  return col.type === 'date' ? 'dateRange' : 'values';
}

/**
 * Range predicate behind `filterVariant: 'dateRange'` column filters.
 * Inclusive on both ends. Two comparison modes, chosen per cell value:
 *
 * - `YYYY-MM-DD` cell values are LOCAL calendar dates (index-grid mappers
 *   emit them via safeFormatDate) — compared lexicographically against the
 *   picked dates' local `yyyy-MM-dd`, never parsed to an instant
 *   (`new Date('YYYY-MM-DD')` is UTC midnight, which shifts a calendar day
 *   for negative-offset timezones).
 * - Anything else parses as a timestamp and compares against the picked
 *   days' local start/end — matching how date cells render timestamps
 *   (safeFormatDate's local getters).
 *
 * Empty/unparseable cells fail any active range (an unknown date can't be
 * proven inside it). An empty range passes everything.
 */
export function isValueInDateRange(raw: unknown, range: ColumnDateRange): boolean {
  if (!range.from && !range.to) return true;
  const str = toDisplayString(raw).trim();
  if (!str) return false;
  if (CALENDAR_DATE_RE.test(str)) {
    if (range.from && str < format(range.from, 'yyyy-MM-dd')) return false;
    if (range.to && str > format(range.to, 'yyyy-MM-dd')) return false;
    return true;
  }
  const instant = new Date(str).getTime();
  if (Number.isNaN(instant)) return false;
  if (range.from && instant < startOfDay(range.from).getTime()) return false;
  if (range.to && instant > endOfDay(range.to).getTime()) return false;
  return true;
}

/**
 * Safely coerce an unknown value to a display string. Unlike `String()`, this
 * returns '' for objects/arrays instead of '[object Object]', so accidental
 * non-primitive cell values don't leak into the UI.
 */
export function toDisplayString(val: unknown): string {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  return '';
}

/**
 * A cell's user-facing label — the ONE resolution behind the sort comparator
 * (`sortRowsByColumn`) and the table download's text cells. Order:
 * `col.displayValue` → `entityLabels` (raw value stringified as the key) →
 * {@link toDisplayString}. `null` / `undefined` / `''` resolve to `''`.
 */
export function resolveCellLabel(
  col: ColumnConfig,
  raw: unknown,
  entityLabels?: ReadonlyMap<string, string>,
): string {
  if (raw == null || raw === '') return '';
  if (col.displayValue) return col.displayValue(raw);
  const str = toDisplayString(raw);
  if (str === '') return '';
  return entityLabels?.get(str) ?? str;
}

/**
 * A cell's value in the table download (`EditableTable` `download` prop):
 * `col.exportValue` when set; else `numeric` → the number (non-finite or
 * empty → null), `checkbox` → the boolean, `date` → the raw string (the
 * export writes a `YYYY-MM-DD` day as an Excel date, anything else as text);
 * else the {@link resolveCellLabel} text (`''` → null).
 */
export function resolveExportCell(
  col: ColumnConfig,
  row: Record<string, unknown>,
  entityLabels?: ReadonlyMap<string, string>,
): TabularCell {
  const raw = row[col.data];
  if (col.exportValue) return col.exportValue(raw, row);
  if (col.type === 'numeric') {
    if (raw == null || raw === '') return null;
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  if (col.type === 'checkbox' && typeof raw === 'boolean') return raw;
  if (col.type === 'date' && typeof raw === 'string') return raw === '' ? null : raw;
  const label = resolveCellLabel(col, raw, entityLabels);
  return label === '' ? null : label;
}

/**
 * Sort rows by one column — the single comparator behind EditableTable's
 * column sort (both the built-in uncontrolled sort and caller-side sorting
 * for controlled mode, see the `sort` prop). Pure; returns a NEW array.
 *
 * Semantics (see {@link ColumnSortState} and README "Column Sorting"):
 *   - Rows whose `row[opts.rowIdField] === undefined` (in-progress new rows)
 *     are pinned first, in input order — never scattered by the sort.
 *   - Empty values (`null`/`undefined`/`''`) sort last in BOTH directions.
 *     This includes a `col.displayValue` resolver (or `entityLabels` entry)
 *     that resolves the raw value to `''` — an empty LABEL is an empty
 *     value, so it can't sort first ascending as a real `''` key would.
 *   - `numeric`/`checkbox` columns compare numerically (`Number(raw)`;
 *     NaN is treated as empty).
 *   - Everything else compares by lowercased display string — the label
 *     {@link resolveCellLabel} resolves — using
 *     `localeCompare(..., { numeric: true })`.
 *   - Equal keys keep their input order (Array.prototype.sort is stable).
 *
 * Callers pass `entityLabels` (value → label) only for entity columns —
 * EditableTable builds it from `entityOptionsMap` internally.
 */
export function sortRowsByColumn<T extends Record<string, unknown>>(
  rows: T[],
  col: ColumnConfig,
  direction: 'asc' | 'desc',
  opts: { rowIdField: string; entityLabels?: ReadonlyMap<string, string> },
): T[] {
  const { rowIdField, entityLabels } = opts;
  const isNumericCompare = col.type === 'numeric' || col.type === 'checkbox';
  const dir = direction === 'asc' ? 1 : -1;

  const sortKey = (row: T): string | number | null => {
    const raw = row[col.data];
    if (raw == null || raw === '') return null;
    if (isNumericCompare) {
      const n = Number(raw);
      return Number.isNaN(n) ? null : n;
    }
    // A resolver (or entity label) that yields '' is an EMPTY key — normalize
    // to null so it takes the empties-last branch (mirrors the raw check above).
    const label = resolveCellLabel(col, raw, entityLabels);
    return label === '' ? null : label.toLowerCase();
  };

  const pinned: T[] = [];
  const sortable: { row: T; key: string | number | null }[] = [];
  for (const row of rows) {
    if (row[rowIdField] === undefined) pinned.push(row);
    else sortable.push({ row, key: sortKey(row) });
  }
  sortable.sort((a, b) => {
    if (a.key === null && b.key === null) return 0;
    if (a.key === null) return 1; // empties last, both directions
    if (b.key === null) return -1;
    if (typeof a.key === 'number' && typeof b.key === 'number') {
      return (a.key - b.key) * dir;
    }
    return String(a.key).localeCompare(String(b.key), undefined, { numeric: true }) * dir;
  });
  return [...pinned, ...sortable.map((s) => s.row)];
}

/**
 * Serialize one clipboard field (a TSV cell) with RFC-4180-style quoting.
 * A field containing a tab, newline, carriage return, or double quote is
 * wrapped in double quotes with any internal quote doubled. This is what
 * lets a multi-line cell (e.g. markdown notes) survive a copy→paste round
 * trip as ONE cell instead of its newlines splitting it across rows — the
 * bug this pair fixes. Matches how Excel / Google Sheets serialize cells,
 * so pasting into or out of a spreadsheet still interops.
 */
export function serializeClipboardCell(value: string): string {
  if (/[\t\n\r"]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Serialize a grid of string cells to clipboard text: cells tab-separated,
 * rows newline-separated, each cell quoted per {@link serializeClipboardCell}.
 */
export function serializeClipboardGrid(rows: string[][]): string {
  return rows.map(row => row.map(serializeClipboardCell).join('\t')).join('\n');
}

/**
 * Parse clipboard TSV text into a grid, honoring RFC-4180-style double
 * quoting so a quoted field may contain tabs and newlines without being
 * split. The inverse of {@link serializeClipboardGrid}; also accepts plain
 * unquoted TSV pasted from spreadsheets (Excel/Sheets quote only when
 * needed — same rule as us). Row breaks outside quotes: `\r\n`, `\n`, or a
 * lone `\r`. Empty input yields `[['']]` (one empty cell), preserving the
 * single-value paste-fill path.
 */
export function parseClipboardGrid(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let fieldStart = true; // no chars consumed yet in the current field

  const endField = () => {
    row.push(field);
    field = '';
    fieldStart = true;
  };
  const endRow = () => {
    endField();
    rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"'; // escaped quote inside a quoted field
          i++;
        } else {
          inQuotes = false; // closing quote
        }
      } else {
        field += ch; // literal tab/newline/etc. inside quotes
      }
      continue;
    }
    if (ch === '"' && fieldStart) {
      inQuotes = true; // opening quote only at field start
      fieldStart = false;
      continue;
    }
    if (ch === '\t') {
      endField();
      continue;
    }
    if (ch === '\n') {
      endRow();
      continue;
    }
    if (ch === '\r') {
      if (text[i + 1] === '\n') i++; // treat \r\n as one break
      endRow();
      continue;
    }
    field += ch;
    fieldStart = false;
  }
  endRow(); // flush the trailing field/row
  return rows;
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Serialize a grid to an HTML `<table>` for the clipboard's `text/html`
 * flavor. Unlike plain-text TSV, an HTML `<td>` unambiguously bounds a cell,
 * so a newline inside it (rendered as `<br>`) can't leak into a new row —
 * which is the ONLY representation Numbers / Excel / Google Sheets reliably
 * honor for multi-line cells (they ignore RFC-4180 quotes in pasted TSV).
 * Written alongside {@link serializeClipboardGrid} so both in-app and
 * spreadsheet paste targets round-trip correctly.
 */
export function serializeClipboardHtml(rows: string[][]): string {
  const body = rows
    .map(
      row =>
        '<tr>' +
        row.map(cell => `<td>${escapeHtml(cell).replace(/\r\n|\r|\n/g, '<br>')}</td>`).join('') +
        '</tr>',
    )
    .join('');
  return `<table><tbody>${body}</tbody></table>`;
}

/** Extract a cell's text, mapping `<br>` and block elements to newlines. */
function htmlCellText(cell: Element): string {
  const clone = cell.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
  // Spreadsheets sometimes wrap wrapped lines in <p>/<div> instead of <br>.
  // Insert the separator newline only BETWEEN sibling blocks — never after the
  // last — so a legitimately-authored trailing newline (a trailing <br>) is
  // preserved instead of being stripped by a blanket trailing-newline removal.
  clone.querySelectorAll('p, div').forEach(block => {
    if (block.nextElementSibling) block.append('\n');
  });
  // textContent decodes entities, drops remaining tags, and preserves any raw
  // newlines already present in the cell.
  return clone.textContent ?? '';
}

/**
 * Parse the clipboard's `text/html` flavor into a grid, preferred over the
 * TSV text on paste because it survives multi-line cells from spreadsheets.
 * Returns `null` when the HTML has no `<table>` (e.g. a plain rich-text
 * paste) so the caller can fall back to {@link parseClipboardGrid}.
 */
export function parseClipboardHtml(html: string): string[][] | null {
  if (typeof DOMParser === 'undefined') return null;
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(html, 'text/html');
  } catch {
    return null;
  }
  const table = doc.querySelector('table') as HTMLTableElement | null;
  // `table.rows` / `row.cells` enumerate only THIS table's rows/cells, never a
  // nested table's — so a cell containing a nested table can't duplicate or
  // shift cells across rows (a descendant `querySelectorAll('tr')` would).
  if (!table || typeof table.rows === 'undefined') return null;
  const rows: string[][] = [];
  for (const tr of Array.from(table.rows)) {
    const cells = Array.from(tr.cells).map(htmlCellText);
    if (cells.length > 0) rows.push(cells);
  }
  return rows.length > 0 ? rows : null;
}

/**
 * Deep comparison of two values for dirty detection
 */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return a === b;
  if (a === undefined || b === undefined) return a === b;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object') return a === b;

  if (Array.isArray(a) !== Array.isArray(b)) return false;

  const aObj = a as Record<string, unknown>;
  const bObj = b as Record<string, unknown>;
  const keysA = Object.keys(aObj);
  const keysB = Object.keys(bObj);

  if (keysA.length !== keysB.length) return false;

  return keysA.every(key => deepEqual(aObj[key], bObj[key]));
}

/**
 * Generate a unique row identity string.
 * Persisted rows use their database ID; new/temp rows get a '_temp:' prefixed UUID.
 */
export function generateRowId(existingId?: string): string {
  if (existingId) return existingId;
  return `_temp:${crypto.randomUUID()}`;
}

/**
 * Create an empty row based on column config
 */
export function createEmptyRow<T>(columns: ColumnConfig[], rowIdField: string): T {
  const row: Record<string, unknown> = {};
  columns.forEach(col => {
    if (col.defaultValue !== undefined) {
      row[col.data] = col.defaultValue;
    } else if (col.type === 'checkbox') {
      row[col.data] = false;
    } else {
      row[col.data] = null;
    }
  });
  row[rowIdField] = undefined; // New rows have no ID
  row._rowId = generateRowId();
  row._isEmptyRow = true;
  return row as T;
}

/**
 * Resolve a column's `readOnly` setting for a specific row. Returns the boolean
 * literal for the boolean form, the predicate's result for the function form,
 * or `undefined` when unset. Callers that special-case the row-level override
 * must compare the result `=== false`, never the raw `col.readOnly` (a function
 * is always truthy).
 */
export function resolveColumnReadOnly(
  col: ColumnConfig,
  rowData?: Record<string, unknown>,
): boolean | undefined {
  if (typeof col.readOnly === 'function') {
    return rowData ? col.readOnly(rowData) : undefined;
  }
  return col.readOnly;
}

/**
 * Check if a row is considered empty (all values null/undefined/empty)
 */
export function isRowEmpty<T extends Record<string, unknown>>(row: T, columns: ColumnConfig[]): boolean {
  if (row._isSeparator) return false;

  return columns.every(col => {
    if (col.type === 'checkbox') return true;
    if (resolveColumnReadOnly(col, row)) return true; // skip computed/readOnly columns
    const value = row[col.data];
    return value === null || value === undefined || value === '';
  });
}

/**
 * Check if a row is a separator row
 */
export function isSeparatorRow<T extends Record<string, unknown>>(row: T): boolean {
  return row._isSeparator === true;
}

/**
 * Strip EditableTable internal row markers (keys prefixed with `_`:
 * `_rowId`, `_isEmptyRow`, `_isSeparator`, `_monthKey`, `_separatorLabel`,
 * etc.) from a row before forwarding it to a persistence layer such as
 * Supabase. The reducer stamps `_isEmptyRow` and `_rowId` on rows that
 * pass through `INIT` / `CELL_CHANGE`, so without this filter PostgREST
 * rejects the payload with `PGRST204: Could not find the '_isEmptyRow'
 * column…`. Always wrap when handing `RowChange.data` to a mutation.
 */
export function stripInternalMarkers<T extends Record<string, unknown>>(row: T): T {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(row)) {
    if (!key.startsWith('_')) out[key] = row[key];
  }
  return out as T;
}
